import {
  CUSTOM_FIELD_ENTITIES,
  CUSTOM_FIELD_ENTITY_LABELS,
  CUSTOM_FIELD_TYPES,
  CUSTOM_FIELD_TYPE_LABELS,
  type CreateCustomFieldBody,
  type CustomFieldAdminDTO,
  type CustomFieldEntity,
  type CustomFieldType,
} from '@hearth/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Button, Card, CardHeader, IconButton, Input, Select, Switch, useConfirm, useToast } from '../../../components/ui';
import { ApiError, api, errorMessage } from '../../../lib/api';

/** Extra fields your team wants on leads, contacts or deals (e.g. “Seats”, “Delivery partner”). */
export function CustomFieldsSection() {
  const toast = useToast();
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const { data } = useQuery({ queryKey: ['settings', 'custom-fields'], queryFn: ({ signal }) => api.get<{ items: CustomFieldAdminDTO[] }>('/api/settings/custom-fields', { signal }) });
  const [draft, setDraft] = useState<{ entity: CustomFieldEntity; label: string; type: CustomFieldType; options: string }>({ entity: 'business', label: '', type: 'text', options: '' });
  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['settings', 'custom-fields'] });
    await queryClient.invalidateQueries({ queryKey: ['meta'] });
  };
  const onError = (error: unknown) => toast.error(errorMessage(error));
  const splitOptions = (text: string) => text.split(',').map((o) => o.trim()).filter(Boolean);
  const create = useMutation({
    mutationFn: () =>
      api.post('/api/settings/custom-fields', {
        entity: draft.entity,
        label: draft.label.trim(),
        type: draft.type,
        options: draft.type === 'select' ? splitOptions(draft.options) : [],
      } satisfies Partial<CreateCustomFieldBody>),
    onSuccess: async () => {
      setDraft({ ...draft, label: '', options: '' });
      await refresh();
    },
    onError,
  });
  const update = useMutation({ mutationFn: ({ id, patch }: { id: string; patch: Record<string, unknown> }) => api.patch(`/api/settings/custom-fields/${id}`, patch), onSuccess: refresh, onError });
  const remove = useMutation({
    mutationFn: ({ id, confirmed }: { id: string; confirmed: boolean }) => api.delete(`/api/settings/custom-fields/${id}`, { confirm: confirmed }),
    onSuccess: refresh,
    onError: async (error, variables) => {
      if (error instanceof ApiError && error.status === 409 && !variables.confirmed) {
        if (await confirm({ title: 'Delete this field and its values?', message: error.message, confirmLabel: 'Delete', danger: true })) remove.mutate({ id: variables.id, confirmed: true });
        return;
      }
      onError(error);
    },
  });

  return (
    <Card>
      <CardHeader title="Custom fields" description="Deactivate a field to hide it without losing what was entered." />
      {CUSTOM_FIELD_ENTITIES.map((entity) => {
        const fields = (data?.items ?? []).filter((f) => f.entity === entity);
        return (
          <div key={entity}>
            <p className="bg-slate-50 px-4 py-1.5 text-xs font-medium uppercase tracking-wide text-slate-500">{CUSTOM_FIELD_ENTITY_LABELS[entity]}</p>
            <ul className="divide-y divide-slate-100">
              {fields.map((field) => (
                <li key={field.id} className="flex flex-wrap items-center gap-2 px-4 py-2 text-[13px]">
                  <Input size="sm" className="w-56" aria-label="Field label" defaultValue={field.label} onBlur={(e) => e.target.value.trim() && e.target.value !== field.label && update.mutate({ id: field.id, patch: { label: e.target.value.trim() } })} />
                  <code className="text-xs text-slate-400">{field.key}</code>
                  <span className="text-xs text-slate-500">{CUSTOM_FIELD_TYPE_LABELS[field.type]}</span>
                  {field.type === 'select' && (
                    <Input
                      size="sm"
                      className="w-72"
                      aria-label="Options"
                      defaultValue={field.options.join(', ')}
                      onBlur={(e) => {
                        const options = splitOptions(e.target.value);
                        if (options.join('|') !== field.options.join('|')) update.mutate({ id: field.id, patch: { options } });
                      }}
                    />
                  )}
                  <Switch size="sm" checked={field.isRequired} onChange={(isRequired) => update.mutate({ id: field.id, patch: { isRequired } })} label="Required" />
                  <Switch size="sm" checked={field.isActive} onChange={(isActive) => update.mutate({ id: field.id, patch: { isActive } })} label="Active" />
                  <span className="ml-auto text-xs text-slate-500">{field.valueCount} values</span>
                  <IconButton icon={Trash2} label={`Delete ${field.label}`} size="sm" variant="ghost" onClick={() => remove.mutate({ id: field.id, confirmed: false })} />
                </li>
              ))}
              {fields.length === 0 && <li className="px-4 py-2 text-xs text-slate-400">No custom fields.</li>}
            </ul>
          </div>
        );
      })}
      <form
        className="flex flex-wrap gap-2 border-t border-slate-100 px-4 py-3"
        onSubmit={(event) => {
          event.preventDefault();
          create.mutate();
        }}
      >
        <Select size="sm" aria-label="Applies to" value={draft.entity} onChange={(e) => setDraft({ ...draft, entity: CUSTOM_FIELD_ENTITIES.find((x) => x === e.target.value) ?? 'business' })}>
          {CUSTOM_FIELD_ENTITIES.map((entity) => (
            <option key={entity} value={entity}>
              {CUSTOM_FIELD_ENTITY_LABELS[entity]}
            </option>
          ))}
        </Select>
        <Input size="sm" className="w-56" aria-label="New field label" placeholder="Seats" value={draft.label} onChange={(e) => setDraft({ ...draft, label: e.target.value })} />
        <Select size="sm" aria-label="Type" value={draft.type} onChange={(e) => setDraft({ ...draft, type: CUSTOM_FIELD_TYPES.find((x) => x === e.target.value) ?? 'text' })}>
          {CUSTOM_FIELD_TYPES.map((type) => (
            <option key={type} value={type}>
              {CUSTOM_FIELD_TYPE_LABELS[type]}
            </option>
          ))}
        </Select>
        {draft.type === 'select' && <Input size="sm" className="w-72" aria-label="Options" placeholder="Options, comma-separated" value={draft.options} onChange={(e) => setDraft({ ...draft, options: e.target.value })} />}
        <Button size="sm" type="submit" icon={Plus} loading={create.isPending} disabled={!draft.label.trim() || (draft.type === 'select' && splitOptions(draft.options).length === 0)}>
          Add field
        </Button>
      </form>
    </Card>
  );
}
